import type {
  InstallPresetsOptions,
  InstallPresetsResult
} from '@cli-types/next-adapter/runtime.js'
import type {
  BetterstartPresetId,
  InstalledPresetManifest
} from '@cli-types/preset-engine/presets.js'
import { unique } from '@cli-utils/shared.js'
import { writeConfigFile } from '@core-engine/config/serialize/write-config-file.js'
import { flattenEnvKeys } from '@core-engine/installable/protocol/flatten-env-keys.js'
import { loadSchema } from '@core-engine/schema/schema-reader/load-schema.js'
import { ensureSnapshotGitFiles } from '@core-engine/snapshots/gitignore/ensure-snapshot-git-files.js'
import { presetManifestStore } from '@preset-engine/manifests/preset-manifest-store.js'
import { setSchemaOwner } from '@preset-engine/ownership/set-schema-owner.js'
import { installDependencyDelta } from '@next-adapter/installable-runtime/install-dependency-delta.js'
import { getPresetDefinition } from '@next-adapter/preset-registry.js'
import { applyGeneratedSchema } from './apply-generated-schema.js'
import { configurePreset } from './configure-preset.js'
import { ensurePresetSchemaTargetsAvailable } from './ensure-preset-schema-targets-available.js'
import { getSchemasDir } from './get-schemas-dir.js'
import { normalizeInstalledPresets } from './normalize-installed-presets.js'
import { presetFiles } from './preset-files.js'
import { regenerateBarrels } from './regenerate-barrels.js'
import { resolvePresetInstallOrder } from './resolve-preset-install-order.js'
import { writePresetSchemas } from './write-preset-schemas.js'

export async function installPresets({
  cwd,
  config,
  pm,
  presetIds,
  includeLinter,
  dependenciesInstalled = false
}: InstallPresetsOptions): Promise<InstallPresetsResult> {
  const orderedPresetIds = resolvePresetInstallOrder(presetIds)
  const installedPresetIds = normalizeInstalledPresets(config)
  const skipped: BetterstartPresetId[] = []
  const installed: BetterstartPresetId[] = []
  const warnings: string[] = []

  ensureSnapshotGitFiles(cwd)

  for (const presetId of orderedPresetIds) {
    if (installedPresetIds.includes(presetId)) {
      skipped.push(presetId)
      continue
    }

    const definition = getPresetDefinition(presetId)
    ensurePresetSchemaTargetsAvailable(cwd, config, definition)
    presetFiles.ensureDirectFileTargetsAvailable(cwd, config, definition)

    const configureResult = configurePreset(definition, cwd)
    await installDependencyDelta({
      cwd,
      pm,
      installableId: presetId,
      includeLinter,
      dependenciesInstalled,
      next: {
        presetIds: [...installedPresetIds, presetId],
        integrationIds: config.integrations.installed
      },
      current: { presetIds: installedPresetIds, integrationIds: config.integrations.installed }
    })

    const writtenFiles = presetFiles.writeDirectFiles(cwd, config, definition)
    const writtenSchemas = writePresetSchemas(cwd, config, definition)

    for (const schemaName of writtenSchemas) {
      const loaded = loadSchema(getSchemasDir(cwd, config), schemaName)
      await applyGeneratedSchema(cwd, config, loaded, presetId)
      setSchemaOwner(cwd, schemaName, `preset:${presetId}`)
    }

    if (writtenSchemas.length > 0) {
      await regenerateBarrels(cwd, config)
    }

    installedPresetIds.push(presetId)
    config.presets.installed = unique(installedPresetIds).sort()

    const manifest: InstalledPresetManifest = {
      id: presetId,
      version: definition.version,
      kind: definition.kind,
      ownedSchemas: writtenSchemas,
      ownedFiles: writtenFiles,
      envKeys: flattenEnvKeys(definition),
      packageDependencies: definition.packageDependencies,
      devDependencies: definition.devDependencies,
      configured: configureResult.configured,
      dependencies: definition.dependencies,
      conflicts: definition.conflicts,
      installedAt: new Date().toISOString()
    }

    presetManifestStore.save(cwd, manifest)
    writeConfigFile(cwd, config)
    installed.push(presetId)
  }

  return {
    installed,
    skipped,
    warnings,
    config
  }
}
